/**
 * EditQuotaPill.jsx — shows the scribe's remaining edits on a piece
 * of content. Feb 12 2026 user spec: *"add one edit opportunity for
 * writers and unlimited for High King and Kings"* + *"yes — show
 * 1 edit remaining / 0 edits remaining pill on each item"*.
 *
 * Renders:
 *   - "Unlimited edits" pill for Kings + High King.
 *   - "N edits remaining" pill for writers with quota left (green).
 *   - "0 edits left — Request edit" pill for exhausted writers, which
 *     opens the request modal below.
 *
 * Props:
 *   - itemKind: see edit_quota.ITEM_KIND_MAP
 *   - itemId:   the doc id
 *   - className: optional Tailwind classes
 *   - compact:  when true, renders just a small pill (no labels)
 *   - testidPrefix: optional override for the data-testid
 */
import React, { useEffect, useState } from 'react';
import { Crown, Pencil, AlertCircle, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditQuotaPill = ({ itemKind, itemId, className = '', compact = false, testidPrefix = 'edit-quota' }) => {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!itemKind || !itemId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/edit-quota/${itemKind}/${itemId}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setSnap(d);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [itemKind, itemId]);

  const submitRequest = async () => {
    if (!reason.trim()) {
      toast.error('Tell the King why you need this edit');
      return;
    }
    setRequesting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/writer/edit-grants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_kind: itemKind, item_id: itemId, reason: reason.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Could not submit request');
        return;
      }
      toast.success('Edit request sent to your King 👑');
      setShowRequest(false);
      setReason('');
    } catch {
      toast.error('Network error');
    } finally {
      setRequesting(false);
    }
  };

  if (loading || !snap) return null;

  // High King + Kings — unlimited.
  if (snap.bypass) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-200 text-[9px] font-black uppercase tracking-widest ${className}`}
        data-testid={`${testidPrefix}-unlimited`}
        title="Kings have unlimited edits"
      >
        <Crown className="w-2.5 h-2.5" />
        {compact ? '∞' : 'Unlimited edits'}
      </span>
    );
  }

  const remaining = snap.remaining ?? 0;
  if (remaining > 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-200 text-[9px] font-black uppercase tracking-widest ${className}`}
        data-testid={`${testidPrefix}-remaining`}
        title={`${remaining} edit${remaining === 1 ? '' : 's'} remaining`}
      >
        <Pencil className="w-2.5 h-2.5" />
        {compact ? remaining : `${remaining} edit${remaining === 1 ? '' : 's'} left`}
      </span>
    );
  }

  // Exhausted — show the "request edit" CTA + modal.
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setShowRequest(true); }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-rose-400/40 bg-rose-400/10 text-rose-200 hover:bg-rose-400/20 text-[9px] font-black uppercase tracking-widest transition ${className}`}
        data-testid={`${testidPrefix}-request-btn`}
        title="No edits left — request an edit from a King"
      >
        <AlertCircle className="w-2.5 h-2.5" />
        {compact ? '0 · request' : '0 left · request edit'}
      </button>
      {showRequest && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm grid place-items-center px-4"
          onClick={() => setShowRequest(false)}
          data-testid={`${testidPrefix}-request-modal`}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-[#12131A] p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-black text-white tracking-tight">
                Request edit from a King<span className="text-amber-300">.</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowRequest(false)}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
                data-testid={`${testidPrefix}-request-close`}
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>
            <p className="text-xs text-white/60 leading-relaxed">
              You've used all your edits on this item. Tell a King what you'd like to change
              — they'll review and grant an extra edit if it's a worthwhile fix.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why do you need to edit this? (e.g. fix typos, reword a paragraph, replace cover image)"
              className="w-full h-28 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-amber-400/60 focus:outline-none resize-none"
              maxLength={500}
              data-testid={`${testidPrefix}-request-reason`}
            />
            <div className="flex items-center justify-between text-[10px] text-white/40">
              <span>{reason.length}/500</span>
              <span>Asks the King or High King to approve</span>
            </div>
            <button
              type="button"
              onClick={submitRequest}
              disabled={requesting || !reason.trim()}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black px-6 py-2.5 text-sm uppercase tracking-widest"
              data-testid={`${testidPrefix}-request-submit`}
            >
              {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send to King
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EditQuotaPill;
